import React from 'react';
import clsx from 'clsx';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'accent' | 'neutral';
  className?: string;
  showText?: boolean;
  text?: string;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8', 
  lg: 'h-12 w-12',
  xl: 'h-16 w-16'
};

const variantClasses = {
  primary: 'border-rm-primary-700',
  accent: 'border-rm-accent-600',
  neutral: 'border-rm-neutral-600'
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'accent',
  className,
  showText = false,
  text = 'Loading...'
}) => {
  return (
    <div className={clsx('flex flex-col items-center justify-center space-y-3', className)}>
      <div
        className={clsx(
          'animate-spin rounded-full border-2 border-transparent',
          sizeClasses[size],
          variantClasses[variant]
        )}
        style={{
          borderTopColor: 'currentColor',
          borderRightColor: 'transparent',
          borderBottomColor: 'transparent', 
          borderLeftColor: 'transparent'
        }}
      />
      {showText && (
        <p className="text-sm text-rm-neutral-600 font-medium">
          {text}
        </p>
      )}
    </div>
  );
};

interface LoadingPageProps {
  title?: string;
  subtitle?: string;
  variant?: 'primary' | 'accent' | 'neutral';
}

export const LoadingPage: React.FC<LoadingPageProps> = ({
  title = 'Loading your experience',
  subtitle = 'Setting up your personalized digital wellbeing assistant...',
  variant = 'accent'
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-rm-bg-0 px-4">
      <div className="max-w-md w-full text-center space-y-8">
        {/* Brand Logo Area */}
        <div className="space-y-4">
          <div className="w-20 h-20 mx-auto bg-rm-primary-700 rounded-card flex items-center justify-center">
            <div className="w-12 h-12 bg-rm-cta rounded-card opacity-90"></div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-rm-primary-700">
              {title}
            </h1>
            <p className="text-sm text-rm-neutral-600 leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>

        {/* Loading Spinner */}
        <LoadingSpinner 
          size="lg" 
          variant={variant}
          className="py-4"
        />

        {/* Progress Dots */}
        <div className="flex justify-center space-x-2">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className={clsx(
                'w-2 h-2 rounded-full',
                'animate-pulse',
                variant === 'accent' ? 'bg-rm-accent-600' : 
                variant === 'primary' ? 'bg-rm-primary-700' : 'bg-rm-neutral-600'
              )}
              style={{
                animationDelay: `${index * 0.2}s`,
                animationDuration: '1.4s'
              }}
            />
          ))}
        </div>

        {/* Subtle Brand Message */}
        <div className="pt-8 border-t border-rm-neutral-600/10">
          <p className="text-xs text-rm-neutral-600">
            Powered by <span className="font-medium text-rm-primary-700">Renewmyday & CoInt.ai</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;